import { useEffect, useState } from "react";
import Loader from "../loader/loader";
import AppLayout from "../layout/AppLayout";
import regenerateToken from "../regenerateToken";
import Cookies from "js-cookie";
import { positions, useAlert } from "react-alert";
import { Spinner } from "react-bootstrap";
import { FaGlobe, FaKey, FaListAlt, FaPlus, FaTrashAlt, FaCheckCircle, FaExchangeAlt } from 'react-icons/fa';
import './webhookConfig.css';
import axios from 'axios';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

const WebhookConfig = () => {
  const alert = useAlert();
  const [loader, setLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [authType, setAuthType] = useState("NA");
  const [fields, setFields] = useState([]);
  const [webhookType, setWebhookType] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");

  useEffect(() => {
    getWebhookConfig();
  }, []);

  const createFields = (data) => {
    let result = [];
    for (let key in data) {
      result.push({ key: key, value: data[key] });
    }
    return result;
  };

  const updateExistingStateFronGet = (data) => {
    setWebhookType(data?.type ? data.type : "");
    setAuthType(data?.auth ? data.auth : "NA");
    setWebhookUrl(data?.url ? data.url : "");
    if (data?.headers) {
      let updatedFields = createFields(data.headers);
      setFields(updatedFields);
    }
  };

  /**
   * Normalises the response from either axios or native fetch.
   * - axios response: data already parsed in response.data (admin path)
   * - fetch response: must call response.json() to parse (client path)
   */
  const parseResponse = async (response) => {
    if (typeof response.json === 'function') {
      return await response.json(); // native fetch
    }
    return response.data; // axios — already parsed
  };

  async function getWebhookConfig() {
    setLoader(true);
    try {
      const existingStateConfigResponse = await getStateConfiguration();

      const statusCode = existingStateConfigResponse.status;
      if (statusCode === 401) {
        // API Call is to be made for regenerating the ID Token.
        await regenerateToken();
        const existingStateConfigResponse = await getStateConfiguration();
        const data = await parseResponse(existingStateConfigResponse);
        if (data?.data) {
          updateExistingStateFronGet(data.data);
        } else {
          alert.error(<span className="alertError">{data?.message}</span>, {
            positions: positions.TOP_CENTER,
          });
        }
      } else {
        const data = await parseResponse(existingStateConfigResponse);
        if (data?.data) {
          updateExistingStateFronGet(data.data);
        }
      }
    } catch (error) {
      console.log("error", error.message);
      alert.error(
        <span className="alertError">
          Failed to load webhook data. Please try again
        </span>,
        {
          positions: positions.TOP_CENTER,
        }
      );
    } finally {
      setLoader(false);
    }
  }

  const getStateConfiguration = async () => {
    // ── ADMIN PATH: use admin token (axios) + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "GET",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/dispatch",
      }));
    }
    // ── CLIENT PATH (unchanged — uses native fetch) ────────────────────
    const idToken = Cookies.get("idToken");
    const url =
      "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/dispatch";
    const options = {
      headers: {
        authorization: idToken,
      },
      method: "GET",
    };
    const existingStateConfigResponse = await fetch(url, options);
    return existingStateConfigResponse;
  };

  const getHeadersData = () => {
    let result = {};
    if (fields.length) {
      fields.forEach((each) => {
        result[each.key] = each.value;
      });
    }
    return result;
  };

  const postWebhookData = async () => {
    let payload = {
      dispatchConfig: {
        auth: authType,
        headers: getHeadersData(),
        type: webhookType,
        url: webhookUrl,
      },
    };
    // ── ADMIN PATH: use admin token (axios) + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "POST",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/dispatch",
        data: payload,
      }));
    }
    // ── CLIENT PATH (unchanged — uses native fetch) ────────────────────
    const idToken = Cookies.get("idToken");
    const apiUrl =
      " https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/dispatch";

    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken,
      },
      body: JSON.stringify(payload),
    };

    const response = await fetch(apiUrl, options);

    return response;
  };

  const submitHandler = async (e) => {
    e.preventDefault();
    setSubmitLoader(true);
    try {
      const response = await postWebhookData();
      if (response.status === 401) {
        await regenerateToken();
        const response = await postWebhookData();
        const data = await parseResponse(response);
        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      } else {
        const data = await parseResponse(response);
        console.log("post response", data);
        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      }
    } catch (e) {
      alert.error(
        <span className="alertError">
          Something went wrong. Please try again.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setSubmitLoader(false);
    }
  };

  const addHeaderFields = (e) => {
    e.preventDefault();
    setFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const handleHeaderData = (index, type, value) => {
    const updatedData = [...fields];
    updatedData[index][type] = value;
    setFields(updatedData);
  };

  const removeFields = (e, index) => {
    e.preventDefault();
    const filteredData = fields.filter((each, idx) => idx != index);
    setFields(filteredData);
  };

  return (
    <AppLayout>
      {loader ? (
        <Loader />
      ) : (
        <div className="wh-container">
          <form onSubmit={submitHandler}>
            {/* Header Section */}
            <header className="wh-header">
              <div className="wh-title-group">
                <h1 className="wh-title">Webhook Configuration</h1>
                <p className="wh-subtitle">Manage your webhook integration and dispatch settings.</p>
              </div>
              <div className="wh-actions-top">
                <button
                  className="wh-btn-primary"
                  type="submit"
                  disabled={submitLoader}
                >
                  {submitLoader ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FaCheckCircle /> Confirm Changes
                    </>
                  )}
                </button>
              </div>
            </header>

            {/* Main Grid Layout */}
            <div className="wh-bento-grid">

              {/* Card 1: Core Configuration */}
              <div className="wh-card">
                <div className="wh-card-header">
                  <span className="d-flex align-items-center gap-2">
                    <FaGlobe style={{ color: '#3b82f6' }} />
                    <h3 className="wh-card-title">Core Settings</h3>
                  </span>
                </div>
                <div className="wh-card-body">
                  {/* Webhook Type */}
                  <div className="wh-form-group">
                    <label className="wh-label">Webhook Type</label>
                    <select
                      className="wh-select"
                      value={webhookType}
                      onChange={(e) => setWebhookType(e.target.value)}
                    >
                      <option value="" disabled hidden>Select an Option</option>
                      <option value="url">URL Delivery</option>
                      <option value="return">Direct Return</option>
                    </select>
                  </div>

                  {/* Webhook URL */}
                  <div className="wh-form-group">
                    <label className="wh-label">Endpoint URL</label>
                    <div style={{ position: 'relative' }}>
                      <FaExchangeAlt style={{ position: 'absolute', left: '12px', top: '14px', color: '#94a3b8' }} />
                      <input
                        className="wh-input"
                        style={{ paddingLeft: '36px' }}
                        value={webhookUrl}
                        onChange={(e) => setWebhookUrl(e.target.value)}
                        placeholder="https://api.example.com/webhook"
                      />
                    </div>
                  </div>

                  {/* Auth Type */}
                  <div className="wh-form-group">
                    <label className="wh-label">Authentication Method</label>
                    <select
                      className="wh-select"
                      value={authType}
                      onChange={(e) => setAuthType(e.target.value)}
                    >
                      <option value="NA">No Authentication</option>
                      <option value="token">Bearer Token</option>
                      <option value="apiKey">API Key</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Card 2: Custom Headers */}
              <div className="wh-card">
                <div className="wh-card-header">
                  <span className="d-flex align-items-center gap-2">
                    <FaKey style={{ color: '#f59e0b' }} />
                    <h3 className="wh-card-title">Custom Headers</h3>
                  </span>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {fields.length} active
                  </span>
                </div>
                <div className="wh-card-body">
                  {fields.length === 0 ? (
                    <div className="wh-empty-state">
                      No custom headers added yet.
                    </div>
                  ) : (
                    <div className="wh-headers-list">
                      {fields.map((each, idx) => (
                        <div className="wh-header-row" key={idx}>
                          <input
                            className="wh-input-sm"
                            placeholder="Key"
                            value={each.key}
                            onChange={(e) => handleHeaderData(idx, "key", e.target.value)}
                          />
                          <input
                            className="wh-input-sm"
                            placeholder="Value"
                            value={each.value}
                            onChange={(e) => handleHeaderData(idx, "value", e.target.value)}
                          />
                          <button
                            className="wh-btn-delete"
                            onClick={(e) => removeFields(e, idx)}
                            title="Remove Header"
                            type="button"
                          >
                            <FaTrashAlt size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button className="wh-btn-add" onClick={addHeaderFields} type="button">
                    <FaPlus size={12} /> Add new key-value pair
                  </button>
                </div>
              </div>

            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
};

export default WebhookConfig;
